import {
    Text,
    Modal,
    Box,
    Button,
    Flex,
    HStack,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    useBreakpointValue,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    Checkbox,
} from '@chakra-ui/react'
import React, { useEffect, useMemo, useState } from 'react'
import { useActionItems } from '~contexts/action-item-provider'
import { useClinic } from '~contexts/clinic-context'
import DenialReasonForm from '~components/shared/denial-reason-form'
import ConfirmModal, { ModalType } from '~components/shared/confirm-modal'
import {
    AppointmentStatus,
    RescheduleFieldsFragment,
    RescheduleStatus,
    useSearchAppointmentsQuery,
    useApproveRescheduleMutation,
    useDenyRescheduleMutation,
    useUpdateRescheduleMutation,
} from '~graphql/generated/graphql'
import {
    useGenericToasts,
    useToastFeedback,
} from '~utils/hooks/use-toast-feedback'
import AppointmentOverview from '../confirm-modal/appointment-overview'
import SchedulePreview from '../confirm-modal/schedule-preview'
import { addMinutes, areIntervalsOverlapping, startOfDay } from 'date-fns'
import RequestedTimeConflicts from './requested-time-conflicts'

const RESCHEDULE_CONFLICT_ERROR_MESSAGE =
    'This reschedule could not be approved because the requested time is no longer available.'

/** Fallback copy when the server surfaces a known unavailability reason code. */
const RESCHEDULE_UNAVAILABLE_REASON_MESSAGES: Record<string, string> = {
    OUTSIDE_BOOKING_WINDOW:
        "The requested time is outside this clinic's booking window (e.g. within the minimum scheduling lead time). Re-approve with the lead-time override to book it anyway, or ask the client to choose a different time.",
    CONFLICT:
        'The requested time is no longer available. Approving would double-book the calendar — ask the client to choose a new time, or deny the request.',
    OUTSIDE_HOURS:
        'The requested time falls outside clinic or clinician working hours (or adjusted availability) for that day. Ask the client to choose a time within available hours, or deny the request.',
    SCHEDULING_RULE:
        'The requested time is blocked by a scheduling rule for this service or clinician. Ask the client to choose a different time, or deny the request.',
    SLOT_UNAVAILABLE:
        'The requested time is no longer bookable for this appointment. Ask the client to choose a new time, or deny the request.',
}

interface Props {
    reschedule: RescheduleFieldsFragment
    isOpen: boolean
    onClose: () => void
}

const RescheduleModal: React.FC<Props> = ({ reschedule, isOpen, onClose }) => {
    const { refetch } = useActionItems()
    const { clinic } = useClinic()
    const modalSize = useBreakpointValue({ base: 'full', md: '3xl' })

    // The clinic's minimum booking lead time ("How far in advance are
    // appointments required to be scheduled?"). The server enforces this window
    // when approving a reschedule, so a request for a time inside it is blocked
    // unless staff explicitly approve skipping the scheduling-date bounds.
    const leadTimeMinutes = clinic?.settings?.scheduling_min_minutes_away || 0
    const requestedStartTime = reschedule.requested_start_time
    const isWithinLeadTime =
        !!requestedStartTime &&
        leadTimeMinutes > 0 &&
        requestedStartTime - Date.now() < leadTimeMinutes * 60 * 1000

    // Explicit staff approval to book inside the lead-time window. Required
    // before the requested time can be confirmed when it falls inside it.
    const [skipLeadTime, setSkipLeadTime] = useState<boolean>(false)

    const confirmToasts = useToastFeedback(
        'Successfully confirmed reschedule!',
        'Error confirming reschedule.',
    )
    const genericToasts = useGenericToasts()
    const denyToasts = useToastFeedback(
        'Reschedule denied.',
        'Error denying reschedule.',
    )
    const resolveToasts = useToastFeedback(
        'Reschedule marked as resolved.',
        'Error marking reschedule as resolved.',
    )

    const [explanation, setExplanation] = useState<string>('')
    const [isDenying, setIsDenying] = useState<boolean>(false)
    const [isResolveOpen, setIsResolveOpen] = useState<boolean>(false)

    // The reason a confirm attempt was rejected by the server (e.g. the slot was
    // taken before approval, or the time is inside the booking window). Surfaced
    // inline so staff see exactly why the reschedule couldn't be confirmed.
    const [approveError, setApproveError] = useState<string | null>(null)

    // Reset per-request state whenever the modal is closed so a prior approval's
    // lead-time override, denial view, or error doesn't carry over to the next
    // open.
    useEffect(() => {
        if (!isOpen) {
            setSkipLeadTime(false)
            setIsDenying(false)
            setApproveError(null)
        }
    }, [isOpen])

    const [approveReschedule, { loading: approveLoading }] =
        useApproveRescheduleMutation()
    const [denyReschedule, { loading: denyLoading }] =
        useDenyRescheduleMutation()
    const [updateReschedule, { loading: updateLoading }] =
        useUpdateRescheduleMutation()

    // Preview/conflict checks fall back to the current appointment when a
    // requested start isn't set. Lead-time gating above uses only the request.
    const previewStartTime =
        reschedule.requested_start_time ?? reschedule.appointment.start_time
    const previewClinician =
        reschedule.requested_clinician && reschedule.appointment.clinician
            ? {
                  ...reschedule.appointment.clinician,
                  id: reschedule.requested_clinician.id,
                  name: reschedule.requested_clinician.name,
              }
            : reschedule.appointment.clinician
    const previewClinicianId = previewClinician?.id
    const requestedRoomId = reschedule.appointment.room?.id
    const previewEndTime = addMinutes(
        previewStartTime,
        reschedule.appointment.minutes,
    ).getTime()

    const { data: appointmentData, loading: approvalConflictLoading } =
        useSearchAppointmentsQuery({
            variables: {
                searchInput: {
                    clinicId: clinic?.id || '',
                    start_date: startOfDay(previewStartTime).getTime(),
                    end_date: addMinutes(
                        startOfDay(previewStartTime),
                        24 * 60,
                    ).getTime(),
                    limit: 100,
                },
            },
            skip: !clinic?.id || !isOpen,
            fetchPolicy: 'no-cache',
        })

    const usesRoomsToSchedule =
        !!clinic?.settings?.does_use_rooms_to_schedule

    const hasApprovalConflict = useMemo(() => {
        // Only treat room overlap as a conflict when the clinic schedules by
        // room *and* both sides have a concrete room id. Matching
        // `undefined === undefined` (null rooms) was falsely flagging every
        // room-less appointment on the day — common for Shepherd clinics.
        const otherAppointments =
            appointmentData?.searchAppointments?.filter(appointment => {
                if (appointment.id === reschedule.appointment.id) return false
                if (
                    appointment.status === AppointmentStatus.Cancelled ||
                    appointment.status === AppointmentStatus.Denied
                ) {
                    return false
                }

                const clinicianConflict =
                    !!previewClinicianId &&
                    appointment.clinician?.id === previewClinicianId
                const roomConflict =
                    usesRoomsToSchedule &&
                    !!requestedRoomId &&
                    appointment.room?.id === requestedRoomId

                return clinicianConflict || roomConflict
            }) ?? []

        return otherAppointments.some(appointment =>
            areIntervalsOverlapping(
                {
                    start: appointment.start_time,
                    end: addMinutes(
                        appointment.start_time,
                        appointment.minutes,
                    ),
                },
                {
                    start: previewStartTime,
                    end: previewEndTime,
                },
            ),
        )
    }, [
        appointmentData?.searchAppointments,
        previewEndTime,
        previewClinicianId,
        requestedRoomId,
        previewStartTime,
        reschedule.appointment.id,
        usesRoomsToSchedule,
    ])

    const handleConfirm = () => {
        setApproveError(null)
        if (hasApprovalConflict) {
            genericToasts.onError(RESCHEDULE_CONFLICT_ERROR_MESSAGE)
            return
        }

        approveReschedule({
            variables: {
                id: reschedule.id,
                // Only override the scheduling-date bounds when the requested
                // time is inside the lead-time window and staff opted in.
                skipSchedulingDateBounds: isWithinLeadTime
                    ? skipLeadTime
                    : undefined,
            },
            onCompleted: () => {
                confirmToasts.onCompleted()
                refetch()
                onClose()
            },
            onError: error => {
                // Prefer the server's specific reason over a generic message so
                // staff understand why the confirm failed (collision vs hours
                // vs scheduling rule, etc.). Server messages are prefixed with
                // the reschedule id for logs — strip it before showing staff.
                const graphQLError = error.graphQLErrors?.[0]
                const reason = graphQLError?.extensions?.reason
                const reasonMessage =
                    typeof reason === 'string'
                        ? RESCHEDULE_UNAVAILABLE_REASON_MESSAGES[reason]
                        : undefined
                const rawMessage =
                    graphQLError?.message ||
                    error.message ||
                    'Error confirming reschedule.'
                const cleanMessage =
                    reasonMessage || rawMessage.replace(/^\[[^\]]*\]\s*/, '')
                setApproveError(cleanMessage)
                genericToasts.onError(cleanMessage)
            },
        })
    }

    const handleDeny = (reason: string) => {
        denyReschedule({
            variables: {
                id: reschedule.id,
                reason,
            },
            onCompleted: () => {
                denyToasts.onCompleted()
                refetch()
                onClose()
            },
            onError: denyToasts.onError,
        })
    }

    const handleResolve = () => {
        updateReschedule({
            variables: {
                id: reschedule.id,
                data: {
                    reason_for_denial: 'Marked as resolved',
                    status: RescheduleStatus.Denied,
                },
            },
            onCompleted: () => {
                resolveToasts.onCompleted()
                refetch()
                onClose()
                setIsResolveOpen(false)
            },
            onError: resolveToasts.onError,
        })
    }

    const loading = approveLoading || denyLoading || approvalConflictLoading

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                size={modalSize}
                scrollBehavior="inside"
            >
                <ModalOverlay>
                    <ModalContent>
                        <ModalHeader borderBottomWidth="1px">
                            <Text fontWeight="medium">
                                {isDenying
                                    ? 'Are you sure you want to deny this reschedule?'
                                    : 'Are you sure you want to confirm this reschedule?'}
                            </Text>
                            <Text color="gray.500" fontSize="sm">
                                {`This action cannot be undone.`}
                            </Text>
                        </ModalHeader>
                        <ModalBody
                            px={{ base: 0, md: 6 }}
                            py={{ base: 0, md: 2 }}
                        >
                            <Flex>
                                <Box
                                    overflow="hidden"
                                    h="full"
                                    w="100%"
                                    maxW={isDenying ? '0px' : '100%'}
                                    opacity={isDenying ? 0 : 1}
                                    transition="max-width 0.4s, opacity 0.2s ease-out"
                                >
                                    <Box minW="min(100vw, 702px)">
                                        <Box
                                            flex={1}
                                            px={{ base: 4, md: 0 }}
                                            py={{ base: 4, md: 0 }}
                                        >
                                            <AppointmentOverview
                                                appointment={
                                                    reschedule.appointment
                                                }
                                                reschedule={reschedule}
                                            />
                                        </Box>
                                        <SchedulePreview
                                            appointment={{
                                                ...reschedule.appointment,
                                                clinician: previewClinician,
                                                start_time: previewStartTime,
                                            }}
                                        />
                                        <RequestedTimeConflicts
                                            reschedule={reschedule}
                                        />
                                        {approveError && (
                                            <Box px={{ base: 4, md: 0 }} pt={4}>
                                                <Alert
                                                    status="error"
                                                    variant="left-accent"
                                                    borderRadius="md"
                                                    alignItems="flex-start"
                                                >
                                                    <AlertIcon />
                                                    <Box>
                                                        <AlertTitle fontSize="sm">
                                                            Couldn&apos;t
                                                            confirm this
                                                            reschedule
                                                        </AlertTitle>
                                                        <AlertDescription fontSize="sm">
                                                            {approveError}
                                                        </AlertDescription>
                                                    </Box>
                                                </Alert>
                                            </Box>
                                        )}
                                        {isWithinLeadTime && (
                                            <Box
                                                px={{ base: 4, md: 0 }}
                                                pt={4}
                                            >
                                                <Alert
                                                    status="warning"
                                                    variant="left-accent"
                                                    borderRadius="md"
                                                    flexDir="column"
                                                    alignItems="flex-start"
                                                    gap={3}
                                                >
                                                    <Flex>
                                                        <AlertIcon />
                                                        <Box>
                                                            <AlertTitle fontSize="sm">
                                                                This time is
                                                                within your
                                                                minimum booking
                                                                lead time
                                                            </AlertTitle>
                                                            <AlertDescription fontSize="sm">
                                                                {`The requested time is less than ${formatLeadTime(
                                                                    leadTimeMinutes,
                                                                )} away — closer than this clinic normally allows appointments to be booked. Approving overrides that lead-time limit for this reschedule only. It does not override double-booking protection.`}
                                                            </AlertDescription>
                                                        </Box>
                                                    </Flex>
                                                    <Checkbox
                                                        ml={6}
                                                        isChecked={skipLeadTime}
                                                        onChange={e =>
                                                            setSkipLeadTime(
                                                                e.target
                                                                    .checked,
                                                            )
                                                        }
                                                    >
                                                        <Text fontSize="sm">
                                                            Approve anyway and
                                                            skip the lead-time
                                                            restriction
                                                        </Text>
                                                    </Checkbox>
                                                </Alert>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                                <Box
                                    overflow="hidden"
                                    h="full"
                                    w="100%"
                                    maxW={isDenying ? '100%' : '0px'}
                                    opacity={isDenying ? 1 : 0}
                                    transition="max-width 0.4s, opacity 0.2s ease-out"
                                >
                                    <Box minW="min(100vw, 702px)">
                                        <Box
                                            flex={1}
                                            px={{ base: 4, md: 0 }}
                                            py={{ base: 4, md: 0 }}
                                        >
                                            <AppointmentOverview
                                                appointment={
                                                    reschedule.appointment
                                                }
                                                reschedule={reschedule}
                                                isRequestingChange={isDenying}
                                            />
                                        </Box>
                                        <Box w="full" mt={8}>
                                            <DenialReasonForm
                                                client={
                                                    reschedule.appointment
                                                        .client!
                                                }
                                                patient={
                                                    reschedule.appointment
                                                        .patient!
                                                }
                                                explanation={explanation}
                                                setExplanation={setExplanation}
                                                isForReschedule
                                                isDenial={true}
                                                setIsDenial={() => {}}
                                            />
                                        </Box>
                                    </Box>
                                </Box>
                            </Flex>
                        </ModalBody>
                        <ModalFooter
                            justifyContent="space-between"
                            borderTopWidth="1px"
                        >
                            <Button
                                onClick={() =>
                                    isDenying ? setIsDenying(false) : onClose()
                                }
                            >
                                {isDenying ? 'Back' : 'Cancel'}
                            </Button>
                            <HStack spacing={4}>
                                <Button
                                    colorScheme={'gray'}
                                    onClick={() => setIsResolveOpen(true)}
                                >
                                    Mark as resolved
                                </Button>
                                {!isDenying && (
                                    <Button
                                        colorScheme={'red'}
                                        onClick={() => {
                                            setApproveError(null)
                                            setIsDenying(true)
                                        }}
                                        isLoading={loading}
                                    >
                                        Deny
                                    </Button>
                                )}
                                <Button
                                    colorScheme={isDenying ? 'red' : 'brand'}
                                    isLoading={loading}
                                    isDisabled={
                                        !isDenying &&
                                        isWithinLeadTime &&
                                        !skipLeadTime
                                    }
                                    onClick={() =>
                                        isDenying
                                            ? handleDeny(explanation)
                                            : handleConfirm()
                                    }
                                >
                                    {isDenying ? 'Deny' : 'Confirm'}
                                </Button>
                            </HStack>
                        </ModalFooter>
                    </ModalContent>
                </ModalOverlay>
            </Modal>
            <ConfirmModal
                itemType="reschedule"
                onConfirm={handleResolve}
                isLoading={updateLoading}
                isOpen={isResolveOpen}
                type={ModalType.Warning}
                actionWord="resolve"
                onClose={() => setIsResolveOpen(false)}
                headerOverride="Are you sure you want to mark this appointment request as resolved?"
                helpTextOverride='Only mark this request as resolved if you have entered it or a comparable appointment into your system. This will remove it from the "Pending" list. This action cannot be undone.'
            />
        </>
    )
}

/**
 * Renders a lead-time window in the largest whole unit that divides it evenly
 * (days, then hours, else minutes) for the approval warning copy.
 */
function formatLeadTime(minutes: number): string {
    if (minutes % 1440 === 0) {
        const days = minutes / 1440
        return `${days} day${days === 1 ? '' : 's'}`
    }
    if (minutes % 60 === 0) {
        const hours = minutes / 60
        return `${hours} hour${hours === 1 ? '' : 's'}`
    }
    return `${minutes} minute${minutes === 1 ? '' : 's'}`
}

export default RescheduleModal
